import { useSyncExternalStore } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useI18n, type TranslationKey } from '@/i18n';
import { api, isWaking, subscribeWaking } from '@/lib/api';
import { fullName } from '@/lib/format';

import { Logo } from './Mark';
import {
  IconAlerts,
  IconAssignments,
  IconAudit,
  IconCalendar,
  IconLeave,
  IconRecycleBin,
  IconCompliance,
  IconDashboard,
  IconDocuments,
  IconMessages,
  IconEmployees,
  IconInventory,
  IconVehicles,
  IconJobs,
  IconPayroll,
  IconShifts,
  IconSites,
} from './NavIcons';
import { AppNudges } from './AppNudges';
import { BottomNav } from './BottomNav';
import { PreferenceToggles, SettingsLink } from './Preferences';
import { usePermissions } from '@/features/access/usePermissions';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import type { Section } from '@/lib/api';

import { Avatar, Badge } from './ui';

interface NavItem {
  to: string;
  labelKey: TranslationKey;
  icon: (props: { className?: string }) => JSX.Element;
  end?: boolean;
  /** The permission that decides whether this link renders at all. */
  section: Section;
  /** Renders the open-alert count. Only one item carries it. */
  alertCount?: boolean;
}

/**
 * Grouped rather than one flat list.
 *
 * Thirteen links with no structure is a list people scan instead of read. The groups
 * match how the office actually splits the work: the day's operations, the register of
 * who and what exists, and the compliance paperwork that has deadlines attached.
 */
const NAV: { titleKey: TranslationKey | null; items: NavItem[] }[] = [
  {
    titleKey: null,
    items: [{ to: '/', section: 'dashboard', labelKey: 'nav.dashboard', icon: IconDashboard, end: true }],
  },
  {
    titleKey: 'nav.groupOperations',
    items: [
      { to: '/shifts', section: 'shifts', labelKey: 'nav.shifts', icon: IconShifts },
      { to: '/calendar', section: 'calendar', labelKey: 'nav.calendar', icon: IconCalendar },
      { to: '/leave', section: 'leave', labelKey: 'nav.leave', icon: IconLeave },
      { to: '/assignments', section: 'assignments', labelKey: 'nav.assignments', icon: IconAssignments },
      { to: '/payroll', section: 'payroll', labelKey: 'nav.payroll', icon: IconPayroll },
    ],
  },
  {
    titleKey: 'nav.groupRecords',
    items: [
      { to: '/employees', section: 'employees', labelKey: 'nav.employees', icon: IconEmployees },
      { to: '/sites', section: 'sites', labelKey: 'nav.sites', icon: IconSites },
      { to: '/jobs', section: 'jobs', labelKey: 'nav.jobs', icon: IconJobs },
      { to: '/inventory', section: 'inventory', labelKey: 'nav.inventory', icon: IconInventory },
      { to: '/vehicles', section: 'vehicles', labelKey: 'nav.vehicles', icon: IconVehicles },
    ],
  },
  {
    titleKey: 'nav.groupCompliance',
    items: [
      { to: '/alerts', section: 'alerts', labelKey: 'nav.alerts', icon: IconAlerts, alertCount: true },
      { to: '/compliance', section: 'compliance', labelKey: 'nav.compliance', icon: IconCompliance },
      { to: '/documents', section: 'documents', labelKey: 'nav.documents', icon: IconDocuments },
      { to: '/messages', section: 'messages', labelKey: 'nav.messages', icon: IconMessages },
    ],
  },
  {
    /*
     * Settings is NOT here any more — it lives in the footer next to the language and
     * theme toggles, which is where someone looks for it, and it was invisible here:
     * the whole group is admin-only, so a manager saw no settings link at all and no
     * way to reach the page.
     */
    titleKey: 'nav.groupSystem',
    items: [
      { to: '/recycle-bin', section: 'recycle_bin', labelKey: 'nav.recycleBin', icon: IconRecycleBin },
      { to: '/audit', section: 'audit', labelKey: 'nav.audit', icon: IconAudit },
    ],
  },
];

export function Layout() {
  const { t } = useI18n();
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const user = me.data?.user;
  /*
   * Links are filtered by permission, not by role. A manager granted the activity log
   * sees the link; an administrator whose `settings` was taken away does not. The API
   * enforces the same grid on every request, so this only decides what is drawn.
   */
  const { can } = usePermissions();

  // Refetched on an interval so a certificate that expires overnight shows up without
  // anyone reloading. The nightly scan writes the alerts; this only reads the count.
  const alerts = useQuery({
    queryKey: ['alerts', false],
    queryFn: () => api.alerts(false),
    refetchInterval: 5 * 60 * 1000,
  });
  const counts = alerts.data?.counts;
  const openAlerts = (counts?.critical ?? 0) + (counts?.warning ?? 0);

  return (
    /* h-screen + overflow-hidden on the shell, scrolling confined to <main>. With
       min-h-screen the whole document scrolls, which drags the sidebar's footer down
       past the fold — the nav and the signed-in user should not move when a long table
       scrolls. */
    /* dvh, not vh: on a phone 100vh is the height with the browser's address bar
       hidden, so the shell was taller than the screen and the page scrolled as a whole —
       header off the top, bottom bar off the bottom. 100dvh is what is actually visible. */
    <div className="h-dvh flex overflow-hidden">
{/* Desktop only. On a phone the same destinations are in the bottom bar, where
          a thumb reaches them — see BottomNav. */}
      <aside className="max-lg:hidden w-60 shrink-0 bg-surface border-e border-border flex flex-col h-full">
        <div className="h-16 shrink-0 flex items-center gap-2.5 px-5 border-b border-border">
          <Logo size={42} className="text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            {/* On two lines rather than cut: "HH Electrical…" is the name it is not. */}
            <div className="font-bold text-sm leading-tight">HH Electrical Systems</div>
            <div className="text-[11px] text-subtle leading-tight">{t('nav.portal')}</div>
          </div>
          <NotificationBell align="left" />
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map((group, groupIndex) => {
            const items = group.items.filter((item) => can(item.section, 'read'));
            if (items.length === 0) return null;

            return (
              <div key={group.titleKey ?? 'main'}>
                {/*
                  A hairline above each heading, not just space.
                  Whitespace alone leaves thirteen links reading as one run; a rule plus
                  a heading makes four short lists, and a short list is something you can
                  take in without reading every line. The first group has no heading —
                  the dashboard is not a category — so it gets neither.
                */}
                {group.titleKey ? (
                  <div
                    className={`px-5 pb-1.5 pt-4 ${groupIndex > 0 ? 'mt-3 border-t border-border' : ''}`}
                  >
                    <span className="label-text">{t(group.titleKey)}</span>
                  </div>
                ) : null}

                <div className="px-3 space-y-0.5">
                  {items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          /* The 2px accent bar is what says "you are here"; the tint only
                             supports it. That lets the fill stay quiet in dark mode without
                             the active item becoming hard to spot. */
                          `relative flex items-center gap-2.5 ps-3 pe-3 py-2 rounded text-sm font-medium transition-colors before:absolute before:start-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full ${
                            isActive
                              ? 'bg-[var(--color-primary-soft)] text-primary font-semibold before:bg-primary'
                              : 'text-muted hover:bg-surface-alt hover:text-text before:bg-transparent'
                          }`
                        }
                      >
                        <Icon />
                        <span className="flex-1 truncate">{t(item.labelKey)}</span>
                        {item.alertCount && openAlerts > 0 ? (
                          <Badge tone={(counts?.critical ?? 0) > 0 ? 'danger' : 'warning'}>
                            {openAlerts}
                          </Badge>
                        ) : null}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Who you are, centred, then the controls that belong to you spread beneath.
            Settings is one of them: it is a per-person preference in the same way the
            language and the theme are, and putting it here is the only reason it is
            reachable at all for a manager. */}
        <div className="p-3 shrink-0 border-t border-border">
          {user ? (
            // Avatar beside the name, and the pair centred as a block — not a column,
            // and not pinned to the left edge either.
            <div className="flex justify-center px-2 pb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={fullName(user)} size={36} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{fullName(user)}</div>
                  <div className="text-[11px] text-subtle truncate">{user.email}</div>
                </div>
              </div>
            </div>
          ) : null}

          <PreferenceToggles />
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
{/* Identity and the per-person controls, which on a desktop live in the
            sidebar footer. Centred, because with navigation at the bottom there is
            nothing else competing for this row. */}
        <header className="lg:hidden h-14 shrink-0 flex items-center gap-2.5 px-4 border-b border-border bg-surface">
          <div className="flex-1" />
          <Logo size={30} className="text-primary shrink-0" />
          <span className="font-bold text-sm truncate">HH Electrical Systems</span>
          {/* Settings only. The language and the theme are toggles ON the settings
              page, so putting them in the title bar as well is the same control in two
              places, one of which is unlabelled. */}
          <div className="flex-1 flex justify-end items-center gap-2">
            <NotificationBell align="right" />
            <SettingsLink />
          </div>
        </header>

        <WakingBanner />
        <AppNudges />

        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
          <div className="max-w-[1400px] mx-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </main>

        <BottomNav can={can} alertCount={openAlerts} />
      </div>
    </div>
  );
}

/**
 * A thin strip while the first requests wait for the database to wake. Without it the
 * page just sits on spinners for fifteen seconds and looks stuck.
 */
function WakingBanner() {
  const { t } = useI18n();
  const waking = useSyncExternalStore(subscribeWaking, isWaking);
  if (!waking) return null;
  return (
    <div role="status" className="shrink-0 flex items-center justify-center gap-2 border-b border-border bg-surface-alt px-4 py-1.5 text-xs font-medium text-muted">
      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--color-border-strong)] border-t-primary" />
      {t('common.waking')}
    </div>
  );
}
